import API_BASE_URL from './apiConfig'

/**
 * Payment Service
 * Online payments through Centiwise (MTN / Airtel mobile money and cards).
 *
 * Flow: create the order (orderService.createOrder), then initiatePayment().
 *   - nextAction 'approve_on_phone': the customer approves a prompt on their phone;
 *     poll getPayment() until status is 'paid' or 'failed'.
 *   - nextAction 'redirect': send the browser to redirectUrl (hosted card page). The
 *     customer comes back to /payment/return?paymentId=..., which polls getPayment().
 */

export const FINAL_PAYMENT_STATUSES = ['paid', 'failed']

// Where an in-progress card payment is remembered while the customer is on the card page
const PENDING_PAYMENT_KEY = 'fabritech:pendingPayment'

const readJson = async (response, fallbackMessage) => {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const error = new Error(data.error || fallbackMessage)
    error.status = response.status
    error.payment = data.payment || null
    throw error
  }
  return data
}

/**
 * Payment options the shop can offer right now
 * @returns {Promise<{ enabled: boolean, shopCurrency: string, options: Array<{ currency, rwfPerUnit, methods }> }>}
 */
export const getPaymentMethods = async () => {
  const response = await fetch(`${API_BASE_URL}/payments/methods`)
  return readJson(response, 'Failed to load payment methods')
}

/**
 * Start a payment for an order
 * @param {string} orderId
 * @param {{ method: 'MTN'|'AIRTEL'|'CARD', phone?: string, currency?: string }} params
 * @returns {Promise<{ payment, redirectUrl, nextAction: 'approve_on_phone'|'redirect', reused?: boolean }>}
 */
export const initiatePayment = async (orderId, { method, phone, currency } = {}) => {
  const response = await fetch(`${API_BASE_URL}/payments/orders/${encodeURIComponent(orderId)}/initiate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method, phone, currency })
  })
  return readJson(response, 'Could not start the payment')
}

/**
 * Current status of a payment attempt (pending attempts are re-checked with the gateway)
 * @param {string} paymentId
 * @returns {Promise<{ payment, order: { id, status, paymentStatus, total } }>}
 */
export const getPayment = async (paymentId) => {
  const response = await fetch(`${API_BASE_URL}/payments/${encodeURIComponent(paymentId)}`)
  return readJson(response, 'Failed to check the payment')
}

/** All payment attempts for an order, newest first */
export const getOrderPayments = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/payments/orders/${encodeURIComponent(orderId)}`)
  return readJson(response, 'Failed to load payments')
}

/**
 * Poll a payment until it is paid/failed, the timeout passes, or `signal` aborts.
 * Resolves with the last { payment, order } seen.
 */
export const waitForPayment = async (paymentId, { intervalMs = 4000, timeoutMs = 3 * 60 * 1000, signal, onUpdate } = {}) => {
  const startedAt = Date.now()
  let last = null
  while (!signal?.aborted) {
    try {
      last = await getPayment(paymentId)
      onUpdate?.(last)
      if (FINAL_PAYMENT_STATUSES.includes(last.payment?.status)) return last
    } catch (error) {
      if (error.status === 404) throw error
      // Network hiccup: keep polling
    }
    if (Date.now() - startedAt >= timeoutMs) return last
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
  return last
}

/**
 * Customer-facing text for a payment error. Gateway codes such as "ENDPOINT_ID_NOT_SPECIFIED"
 * are replaced with a friendly message; readable reasons ("Insufficient funds") are kept.
 */
export const paymentErrorMessage = (message) => {
  const text = String(message || '').trim()
  if (!text || /^[A-Z0-9_ -]+$/.test(text)) {
    return 'The payment could not be processed. Please try again or choose another payment method.'
  }
  return text
}

/** Remember a card payment before leaving for the card page (survives the redirect) */
export const rememberPendingPayment = (details) => {
  try {
    sessionStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({ ...details, savedAt: Date.now() }))
  } catch (_) {
    // Storage unavailable (private mode): the return URL still carries the payment id
  }
}

export const readPendingPayment = () => {
  try {
    return JSON.parse(sessionStorage.getItem(PENDING_PAYMENT_KEY) || 'null')
  } catch (_) {
    return null
  }
}

export const clearPendingPayment = () => {
  try {
    sessionStorage.removeItem(PENDING_PAYMENT_KEY)
  } catch (_) {
    // ignore
  }
}

export default {
  getPaymentMethods,
  initiatePayment,
  getPayment,
  getOrderPayments,
  waitForPayment,
  rememberPendingPayment,
  readPendingPayment,
  clearPendingPayment
}
